import { createSelector } from 'reselect'
import { BaseAction, State } from './index'
import { ProfileActions, selectTeamList } from './profile'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'
import { PLATFORM_ENUM } from 'services/api/constants'
import { AmbassadorFilter } from 'services/api/ambassadors'
import { SUPPORTED_HELPDESK_TEAM_TYPE, HELPDESK_URL } from 'services/tooltipEmitter'

export enum FilterActions {
    FILTER_COUNTRY = 'filter/FILTER_COUNTRY',
    FILTER_TEAM = 'filter/FILTER_TEAM',
    FILTER_TEAM_TYPE = 'filter/FILTER_TEAM_TYPE',
    SET_TEAM_COUNTRY_BY_CAMPAIGN = 'filter/SET_TEAM_COUNTRY_BY_CAMPAIGN',
    SAVE_SUBMISSION_FILTERS = 'filter/SAVE_SUBMISSION_FILTERS',
    CLEAR_SUBMISSION_FILTERS = 'filter/CLEAR_SUBMISSION_FILTERS',
    SAVE_AMBASSADOR_FILTERS = 'filter/SAVE_AMBASSADOR_FILTERS',
    CLEAR_AMBASSADOR_FILTERS = 'filter/CLEAR_AMBASSADOR_FILTERS',
    SET_TEAM_TYPE_FILTERS = 'filter/SET_TEAM_TYPE_FILTERS',
    CLEAR_TEAM_TYPE_FILTERS = 'filter/CLEAR_TEAM_TYPE_FILTERS',
    SET_CAMPAIGN_STATUS_FILTERS = 'filter/SET_CAMPAIGN_STATUS_FILTERS',
    CLEAR_CAMPAIGN_STATUS_FILTERS = 'filter/CLEAR_CAMPAIGN_STATUS_FILTERS',
    SAVE_INDIVIDUAL_PERFORMANCE_FILTERS = 'filter/SAVE_INDIVIDUAL_PERFORMANCE_FILTERS',
    CLEAR_INDIVIDUAL_PERFORMANCE_FILTERS = 'filter/CLEAR_INDIVIDUAL_PERFORMANCE_FILTERS',
}

enum SUBMISSION_FILTER_KEY {
    HIGHLIGHTED = 'highlighted',
    MEET_REQUIREMENT = 'meet_requirement',
    IS_VIEWED = 'is_viewed',
    GENDER = 'gender',
    LOCATION = 'location',
    OCCUPATION = 'occupation',
    SOCIAL_MEDIA_FOLLOWER_NUMBER_TIER = 'social_media_follower_number_tier',
    QUESTIONS = 'questions',
    TAGS = 'tags',
    VOTE_TYPE = 'vote_type',
    RESUBMITTED = 'resubmitted',
    COMPLETED_PROGRESS_COUNT = 'completed_progress_count',
}
enum AMBASSADOR_FILTER_KEY {
    GENDER = 'gender',
    FAVORITE = 'favorite',
    PROFILE_TIER = 'profile_tier',
    TAGS = 'tags',
    SOCIAL_MEDIA_FOLLOWER_NUMBER_TIER = 'social_media_follower_number_tiers',
}

enum INDIVIDUAL_PERFORMANCE_FILTER_KEY {
    GENDER = 'gender',
    FAVORITE = 'favorite',
    STATUS = 'status',
    SOCIAL_MEDIA_FOLLOWER_NUMBER_TIER = 'social_media_follower_number_tier',
}

export interface SubmissionFilter {
    key: SUBMISSION_FILTER_KEY
    value?: string // Holder of platform ID and location parent ID
    options: string[]
}

export interface IndividualPerformanceFilter {
    key: string
    value?: string
    options: string[]
}

export type TeamTypeScope = 'campaign_list' | 'submission_list'

interface TeamTypeState {
    isOpen?: boolean
    selected: string[]
}

export type CampaignStatusScope = 'campaign_list' | 'submission_list'

interface CampaignStatusState {
    isOpen?: boolean
    selected: number[]
}

export interface FilterState {
    country: string
    team: string | null
    teamType: Record<TeamTypeScope, TeamTypeState>
    campaignStatus: Record<CampaignStatusScope, CampaignStatusState>
    submissionFilters: SubmissionFilter[]
    individualPerformanceFilters: IndividualPerformanceFilter[]
    ambassadorFilters: AmbassadorFilter[]
}

const initialState: FilterState = {
    country: '',
    team: null,
    teamType: {
        campaign_list: {
            isOpen: false,
            selected: [],
        },
        submission_list: {
            isOpen: false,
            selected: [],
        },
    },
    campaignStatus: {
        campaign_list: {
            isOpen: false,
            selected: [],
        },
        submission_list: {
            isOpen: false,
            selected: [],
        },
    },
    submissionFilters: [],
    individualPerformanceFilters: [],
    ambassadorFilters: [],
}
export const setCountryFilter = (payload: string | null) => ({
    payload,
    type: FilterActions.FILTER_COUNTRY,
})
export const setTeamFilter = (payload: string | null) => ({
    payload,
    type: FilterActions.FILTER_TEAM,
})
export const setTeamCountryByCampaign = (payload: string) => ({
    payload,
    type: FilterActions.SET_TEAM_COUNTRY_BY_CAMPAIGN,
})
export const saveIndividualPerformanceFilters = (payload: IndividualPerformanceFilter[]) => ({
    payload,
    type: FilterActions.SAVE_INDIVIDUAL_PERFORMANCE_FILTERS,
})
export const clearIndividualPerformanceFilters = () => ({
    type: FilterActions.CLEAR_INDIVIDUAL_PERFORMANCE_FILTERS,
})
export const saveSubmissionFilters = (payload: SubmissionFilter[]) => ({
    payload,
    type: FilterActions.SAVE_SUBMISSION_FILTERS,
})
export const clearSubmissionFilters = () => ({
    type: FilterActions.CLEAR_SUBMISSION_FILTERS,
})
export const saveAmbassadorFilters = (payload: AmbassadorFilter[]) => ({
    payload,
    type: FilterActions.SAVE_AMBASSADOR_FILTERS,
})
export const clearAmbassadorFilters = () => ({
    type: FilterActions.CLEAR_AMBASSADOR_FILTERS,
})

export const setTeamTypeFilters = (payload: Partial<FilterState['teamType']>) => ({
    payload,
    type: FilterActions.SET_TEAM_TYPE_FILTERS,
})

export const clearTeamTypeFilters = () => ({
    type: FilterActions.CLEAR_TEAM_TYPE_FILTERS,
})

export const setCampaignStatusFilters = (payload: Partial<FilterState['campaignStatus']>) => ({
    payload,
    type: FilterActions.SET_CAMPAIGN_STATUS_FILTERS,
})

export const clearCampaignStatusFilters = () => ({
    type: FilterActions.CLEAR_CAMPAIGN_STATUS_FILTERS,
})

export const filterReducer = (state: FilterState = initialState, action: BaseAction) => {
    switch (action.type) {
        case FilterActions.FILTER_COUNTRY:
            return { ...state, country: action.payload }
        case FilterActions.FILTER_TEAM:
            return { ...state, team: action.payload }
        case FilterActions.FILTER_TEAM:
            return { ...state, team: action.payload }
        case FilterActions.SAVE_SUBMISSION_FILTERS:
            return { ...state, submissionFilters: action.payload }
        case FilterActions.CLEAR_SUBMISSION_FILTERS:
            return { ...state, submissionFilters: [] }
        case FilterActions.SAVE_AMBASSADOR_FILTERS:
            return { ...state, ambassadorFilters: action.payload }
        case FilterActions.CLEAR_AMBASSADOR_FILTERS:
            return { ...state, ambassadorFilters: [] }
        case FilterActions.SET_TEAM_TYPE_FILTERS:
            return {
                ...state,
                teamType: {
                    ...state.teamType,
                    ...action.payload,
                },
            }
        case FilterActions.CLEAR_TEAM_TYPE_FILTERS:
            return { ...state, teamType: initialState.teamType }
        case FilterActions.SET_CAMPAIGN_STATUS_FILTERS:
            return {
                ...state,
                campaignStatus: {
                    ...state.campaignStatus,
                    ...action.payload,
                },
            }
        case FilterActions.CLEAR_CAMPAIGN_STATUS_FILTERS:
            return { ...state, campaignStatus: initialState.campaignStatus }
        case ProfileActions.LOGOUT_REQUEST:
            return initialState
        case FilterActions.SAVE_INDIVIDUAL_PERFORMANCE_FILTERS:
            return { ...state, individualPerformanceFilters: action.payload }
        case FilterActions.CLEAR_INDIVIDUAL_PERFORMANCE_FILTERS:
            return { ...state, individualPerformanceFilters: [] }
        default:
            return state
    }
}

const selectFilter = (state: State) => state.filter
export const selectCountryFilter = (state: State) => selectFilter(state).country
export const selectTeamTypeFilters = (state: State) => selectFilter(state).teamType
export const selectCampaignStatusFilters = (state: State) => selectFilter(state).campaignStatus
export const selectTeamFilter = (state: State) => selectFilter(state).team
export const selectCurrentTeam = (state: State) => {
    const teamList = selectTeamList(state)
    const teamFilter = selectTeamFilter(state)
    return teamList?.find(team => team.id === teamFilter)
}
export const selectIsHelpdeskSupportedByActiveTeam = createSelector(
    [selectTeamList, selectTeamFilter],
    (teamList, teamFilter) => {
        const currentTeam = teamList?.find(team => team.id === teamFilter)
        return !!HELPDESK_URL && !!currentTeam && SUPPORTED_HELPDESK_TEAM_TYPE.includes(currentTeam.type)
    },
)

const SUBMISSION_FILTER_KEY_MAP: { [key: string]: FIELDS_TYPE } = {
    [SUBMISSION_FILTER_KEY.GENDER]: FIELDS_TYPE.GENDER,
    [SUBMISSION_FILTER_KEY.HIGHLIGHTED]: FIELDS_TYPE.HIGHLIGHT,
    [SUBMISSION_FILTER_KEY.MEET_REQUIREMENT]: FIELDS_TYPE.MEET_REQUIREMENT,
    [SUBMISSION_FILTER_KEY.IS_VIEWED]: FIELDS_TYPE.STATUS,
    [SUBMISSION_FILTER_KEY.LOCATION]: FIELDS_TYPE.LOCATION,
    [SUBMISSION_FILTER_KEY.OCCUPATION]: FIELDS_TYPE.EMPLOYMENT_STATUS,
    [SUBMISSION_FILTER_KEY.TAGS]: FIELDS_TYPE.PARTIPOSTER_TAGS,
    [SUBMISSION_FILTER_KEY.VOTE_TYPE]: FIELDS_TYPE.VOTE_TYPE,
    [SUBMISSION_FILTER_KEY.QUESTIONS]: FIELDS_TYPE.MCQ,
    [SUBMISSION_FILTER_KEY.RESUBMITTED]: FIELDS_TYPE.RESUBMITTED,
    [SUBMISSION_FILTER_KEY.COMPLETED_PROGRESS_COUNT]: FIELDS_TYPE.COMPLETED_PROGRESS_COUNT,
    [SUBMISSION_FILTER_KEY.SOCIAL_MEDIA_FOLLOWER_NUMBER_TIER]: FIELDS_TYPE.SOCIAL_MEDIA_FOLLOWER_NUMBER_TIER,
}

/**
 * Transform the backend filters into objects
 *
 * Filters:
 * location+parentUuid
 * questions+blockUuid
 */
export const selectSubmissionFilters = createSelector(selectFilter, filterState => {
    const submissionFilters = {} as { [key in string]: string[] }
    filterState.submissionFilters?.map(filter => {
        const availableFilterOptions = filter.options
        if (filter.key === SUBMISSION_FILTER_KEY.SOCIAL_MEDIA_FOLLOWER_NUMBER_TIER && filter.value !== undefined) {
            const socialMediaFollowerKey = {
                [PLATFORM_ENUM.IG]: FIELDS_TYPE.IG_FOLLOWERS,
                [PLATFORM_ENUM.FB]: FIELDS_TYPE.FB_FRIENDS,
                [PLATFORM_ENUM.TIKTOK]: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                [PLATFORM_ENUM.TWITTER]: FIELDS_TYPE.TWITTER_FOLLOWERS,
                [PLATFORM_ENUM.XIAOHONGSHU]: FIELDS_TYPE.XIAOHONGSHU_FOLLOWERS,
            }[filter.value]
            if (socialMediaFollowerKey) {
                submissionFilters[socialMediaFollowerKey] = availableFilterOptions
            }
        } else if (
            filter.value !== undefined &&
            [SUBMISSION_FILTER_KEY.QUESTIONS, SUBMISSION_FILTER_KEY.LOCATION].includes(filter.key)
        ) {
            submissionFilters[`${SUBMISSION_FILTER_KEY_MAP[filter.key]}+${filter.value}`] = availableFilterOptions
        } else {
            submissionFilters[SUBMISSION_FILTER_KEY_MAP[filter.key]] = availableFilterOptions
        }
    })

    return submissionFilters
})

const INDIVIDUAL_PERFORMANCE_FILTER_KEY_MAP: { [key: string]: FIELDS_TYPE } = {
    [INDIVIDUAL_PERFORMANCE_FILTER_KEY.GENDER]: FIELDS_TYPE.GENDER,
    [INDIVIDUAL_PERFORMANCE_FILTER_KEY.FAVORITE]: FIELDS_TYPE.FAVORITE,
    [INDIVIDUAL_PERFORMANCE_FILTER_KEY.STATUS]: FIELDS_TYPE.STATUS,
    [INDIVIDUAL_PERFORMANCE_FILTER_KEY.SOCIAL_MEDIA_FOLLOWER_NUMBER_TIER]:
        FIELDS_TYPE.SOCIAL_MEDIA_FOLLOWER_NUMBER_TIER,
}

export const selectIndividualPerformanceFilters = createSelector(selectFilter, filterState => {
    const individualPerformanceFilters = {} as { [key in string]: string[] }

    filterState.individualPerformanceFilters?.map(filter => {
        const availableFilterOptions = filter.options
        individualPerformanceFilters[INDIVIDUAL_PERFORMANCE_FILTER_KEY_MAP[filter.key]] = availableFilterOptions
    })

    return individualPerformanceFilters
})

const AMBASSADOR_FILTER_KEY_MAP: { [key: string]: FIELDS_TYPE } = {
    [AMBASSADOR_FILTER_KEY.GENDER]: FIELDS_TYPE.GENDER,
    [AMBASSADOR_FILTER_KEY.FAVORITE]: FIELDS_TYPE.FAVORITE,
    [AMBASSADOR_FILTER_KEY.PROFILE_TIER]: FIELDS_TYPE.PROFILE_TYPE,
    [AMBASSADOR_FILTER_KEY.TAGS]: FIELDS_TYPE.PARTIPOSTER_TAGS,
}

export const selectAmbassadorFilters = createSelector(selectFilter, filterState => {
    const ambassadorFilters = {} as { [key in string]: string[] }
    filterState.ambassadorFilters?.map(filter => {
        const availableFilterOptions = filter.options
        if (filter.key === AMBASSADOR_FILTER_KEY.SOCIAL_MEDIA_FOLLOWER_NUMBER_TIER && filter.value !== undefined) {
            const socialMediaFollowerKey = {
                [PLATFORM_ENUM.IG]: FIELDS_TYPE.IG_FOLLOWERS,
                [PLATFORM_ENUM.FB]: FIELDS_TYPE.FB_FRIENDS,
                [PLATFORM_ENUM.TIKTOK]: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                [PLATFORM_ENUM.TWITTER]: FIELDS_TYPE.TWITTER_FOLLOWERS,
                [PLATFORM_ENUM.XIAOHONGSHU]: FIELDS_TYPE.XIAOHONGSHU_FOLLOWERS,
            }[filter.value]
            if (socialMediaFollowerKey) {
                ambassadorFilters[socialMediaFollowerKey] = availableFilterOptions
            }
        } else {
            ambassadorFilters[AMBASSADOR_FILTER_KEY_MAP[filter.key]] = availableFilterOptions
        }
    })

    return ambassadorFilters
})
